import { CustomEventWithDetail, html, View } from 'rune-ts';
import klass from './Dropdown.module.scss';
import { each, find, findIndex, map, pipe, toArray, zipWithIndex } from '@fxts/core';

export interface DropdownItemData {
  label: string;
  value: string;
  checked?: boolean;
}

export interface DropdownData {
  name: string;
  items: DropdownItemData[];
  has_empty_option?: boolean;
  placeholder?: string;
}

export interface DropdownOption {
  klass?: string;
  disabled?: boolean;
  readonly?: boolean;
}

const SelectActions = {
  Close: 0,
  CloseSelect: 1,
  First: 2,
  Last: 3,
  Next: 4,
  Open: 5,
  PageDown: 6,
  PageUp: 7,
  Previous: 8,
  Select: 9,
  Type: 10,
};

export const isScrollable = (element: HTMLElement) => {
  return element && element.clientHeight < element.scrollHeight;
};

export const maintainScrollVisibility = (activeElement: HTMLElement, scrollParent: HTMLElement) => {
  const { offsetHeight, offsetTop } = activeElement;
  const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

  const isAbove = offsetTop < scrollTop;
  const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

  if (isAbove) {
    scrollParent.scrollTo(0, offsetTop);
  } else if (isBelow) {
    scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
  }
};

export class Dropdown extends View<DropdownData> {
  options: DropdownItemData[];
  open: boolean;
  activeIndex: number;
  constructor(
    data: DropdownData,
    public option: DropdownOption = {},
  ) {
    super(data);

    const empty_option = [{ value: '', label: this.data.placeholder, checked: false }] as DropdownItemData[];
    this.options = [...(this.data.has_empty_option ? empty_option : []), ...this.data.items];
    this.open = false;

    const active_index = this.options.findIndex((item) => item.checked);
    this.activeIndex = active_index !== -1 ? active_index : 0;
  }

  get value() {
    return this.options[this.activeIndex]?.value;
  }

  set value(val) {
    const index = pipe(
      this.options,
      findIndex((option) => option.value === val),
    );

    this.selectOption(index === -1 ? 0 : index);
    this.onOptionChange(index);
  }

  get displayName() {
    return this.options[this.activeIndex]?.label;
  }

  override template() {
    const { name } = this.data;
    const { disabled, readonly } = this.option;

    const options = pipe(
      this.options,
      zipWithIndex,
      map(
        ([index, item]) =>
          html`<div
            role="option"
            id="${name}_${item.value}"
            name="${name}"
            value="${item.value}"
            class="${klass.option}${item.checked ? ` ` + klass.option_current : ''}"
            data-index="${index}"
            data-placeholder="${item.label === this.data.placeholder}"
            aria-selected="${item.checked}"
          >
            ${item.label}
          </div>`,
      ),
      toArray,
    );

    const has_checked = pipe(
      this.options,
      find(({ checked }) => checked),
    );

    return html`<div
      class="${this.option.klass ?? ''} ${klass.dropdown} ${disabled ? klass.disabled : ''} ${readonly
        ? klass.readonly
        : ''}"
    >
      <div class="${klass.dropdown_input}" aria-expanded="false" aria-haspopup="listbox" role="combobox" tabindex="0">
        ${has_checked ? has_checked.label : this.options[0]?.label}
      </div>
      <div class="${klass.dropdown_menu}" role="listbox" tabindex="-1">${options}</div>
    </div> `;
  }

  override onRender() {
    this.delegate('click', `[role=combobox]`, this.onDropdownClick);
    this.delegate('focusout', '[role=combobox]', this.onDropdownFocusout);
    this.delegate('keydown', '[role=combobox]', ({ originalEvent }) => this.onDropdownKeyDown(originalEvent));
    this.delegate('focusout', '[role=listbox]', this.onDropdownFocusout);
    this.delegate('click', '[role=option]', ({ originalEvent }) => {
      originalEvent.stopPropagation();
      const index = parseInt((originalEvent.target as HTMLDivElement).dataset.index!);
      this.onOptionClick(index);
    });
  }

  private onDropdownClick = () => {
    if (this.open && this.data.has_empty_option) {
      this.activeIndex = 0;
    }
    this.updateMenuState(!this.open, false);
  };

  private onDropdownKeyDown = (e) => {
    const max = this.options.length - 1;

    const action = this.getActionFromKey(e, this.open);

    switch (action) {
      case SelectActions.Last:
      case SelectActions.First:
        this.updateMenuState(true);
        break;
      case SelectActions.Next:
      case SelectActions.Previous:
      case SelectActions.PageUp:
      case SelectActions.PageDown:
        e.preventDefault();
        this.onOptionChange(this.getUpdatedIndex(max, action));
        break;
      case SelectActions.CloseSelect:
        e.preventDefault();
        this.selectOption(this.activeIndex);
        this.updateMenuState(false);
        break;
      case SelectActions.Close:
        e.preventDefault();
        this.updateMenuState(false);
        break;
      case SelectActions.Open:
        e.preventDefault();
        this.updateMenuState(true);
        break;
    }
  };

  private onDropdownFocusout = (e) => {
    const dropdown_menu_el = this.element().querySelector(`.${klass.dropdown_menu}`)!;
    if (dropdown_menu_el.contains(e.relatedTarget)) {
      return;
    }

    if (this.open) {
      this.selectOption(this.activeIndex);
      this.updateMenuState(false, false);
    }
  };

  private onOptionChange = (index: number) => {
    this.activeIndex = index;
    const dropdown_input_el = this.element().querySelector(`.${klass.dropdown_input}`)!;
    dropdown_input_el.setAttribute('aria-activedescendant', `${this.data.name}-${index}`);

    const options = this.element().querySelectorAll('[role=option]');
    pipe(
      [...options],
      each((option_el) => option_el.classList.remove(klass.option_current)),
    );
    const current_option_el = options[index] as HTMLElement;
    current_option_el.classList.add(klass.option_current);

    // 새 옵션이 화면에 표시되도록
    const dropdown_menu_el = this.element().querySelector('[role=listbox]')!;
    if (isScrollable(dropdown_menu_el as HTMLElement)) {
      maintainScrollVisibility(current_option_el, dropdown_menu_el as HTMLElement);
    }
  };

  private onOptionClick = (index: number) => {
    this.onOptionChange(index);
    this.selectOption(index);
    this.updateMenuState(false);
  };

  private selectOption = (index: number) => {
    this.activeIndex = index;

    const selected = this.options[index];
    const dropdown_input_el = this.element().querySelector(`.${klass.dropdown_input}`)!;
    dropdown_input_el.innerHTML = selected?.label ?? '';

    const options = this.element().querySelectorAll('[role=option]');
    pipe(
      options,
      each((option_el) => {
        option_el.setAttribute('aria-selected', 'false');
      }),
    );
    options[index]?.setAttribute('aria-selected', 'true');
  };

  private getUpdatedIndex = (maxIndex: number, action) => {
    const pageSize = 10;

    switch (action) {
      case SelectActions.First:
        return 0;
      case SelectActions.Last:
        return maxIndex;
      case SelectActions.Previous:
        return Math.max(0, this.activeIndex - 1);
      case SelectActions.Next:
        return Math.min(maxIndex, this.activeIndex + 1);
      case SelectActions.PageUp:
        return Math.max(0, this.activeIndex - pageSize);
      case SelectActions.PageDown:
        return Math.min(maxIndex, this.activeIndex + pageSize);
      default:
        return this.activeIndex;
    }
  };

  private getActionFromKey(e: KeyboardEvent, menuOpen: boolean) {
    const { key, altKey, ctrlKey, metaKey } = e;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' '];
    if (!menuOpen && openKeys.includes(key)) {
      return SelectActions.Open;
    }

    if (key === 'Home') {
      return SelectActions.First;
    }
    if (key === 'End') {
      return SelectActions.Last;
    }

    if (
      key === 'Backspace' ||
      key === 'Clear' ||
      (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
    ) {
      return SelectActions.Type;
    }

    if (menuOpen) {
      if (key === 'ArrowUp' && altKey) {
        return SelectActions.CloseSelect;
      } else if (key === 'ArrowDown' && !altKey) {
        return SelectActions.Next;
      } else if (key === 'ArrowUp') {
        return SelectActions.Previous;
      } else if (key === 'PageUp') {
        return SelectActions.PageUp;
      } else if (key === 'PageDown') {
        return SelectActions.PageDown;
      } else if (key === 'Escape') {
        return SelectActions.Close;
      } else if (key === 'Enter' || key === '') {
        return SelectActions.CloseSelect;
      }
    }
  }

  private updateMenuState(open: boolean, callFocus = true) {
    if (this.open === open || this.option.readonly || this.option.disabled) {
      return;
    }

    this.open = open;

    const dropdown_input_el = this.element().querySelector(`.${klass.dropdown_input}`)!;
    dropdown_input_el.setAttribute('aria-expanded', `${open}`);
    open ? this.element().classList.add(klass.open) : this.element().classList.remove(klass.open);

    const activeID = open ? `${this.data.name}-${this.activeIndex}` : '';
    dropdown_input_el.setAttribute('aria-activedescendant', activeID);

    if (activeID === '') {
      dropdown_input_el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    callFocus && (dropdown_input_el as HTMLDivElement).focus();

    if (open) {
      dropdown_input_el.innerHTML = this.data.has_empty_option
        ? this.data.placeholder!
        : this.options[this.activeIndex]!.label;
    } else {
      this.dispatchEvent(DropdownChangeEvent, {
        bubbles: true,
        detail: { selected: this.options[this.activeIndex]! },
      });
    }
  }
}

export class DropdownChangeEvent extends CustomEventWithDetail<{ selected: DropdownItemData }> {}
